import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthDataService } from './@core/services/auth/auth-data.service';
import { PermissionStoreService } from './@core/services/permission/permission-store.service';
import { RuntimeService } from './@core/services/runtime/runtime.service';
import { AuthStoreService } from './auth/auth-store.service';
import {
    MEMBER_MANAGER_REFRESH,
    MEMBER_MANAGER_TOKEN,
} from './shared/consts/storage.const';

export function appInitializer() {
    const runtimeService = inject(RuntimeService);
    const router = inject(Router);
    const authDataService = inject(AuthDataService);
    const authStoreService = inject(AuthStoreService);
    const permissionStoreService = inject(PermissionStoreService);

    return () => {
        return new Promise<void>(async (resolve) => {
            try {
                let appConfig = runtimeService.getCachedAppConfigByAppKey();

                if (appConfig) {
                    runtimeService.refreshAppConfigByAppKey().subscribe({
                        error: (error) => {
                            console.error(
                                'Failed to refresh app config:',
                                error,
                            );
                        },
                    });
                    runtimeService.getCustomerApps().subscribe({
                        error: (error) => {
                            console.error(
                                'Failed to refresh customer apps:',
                                error,
                            );
                        },
                    });
                } else {
                    appConfig = await firstValueFrom(
                        runtimeService.refreshAppConfigByAppKey(),
                    );
                    await firstValueFrom(runtimeService.getCustomerApps());
                }

                if (appConfig) {
                    const favicon = runtimeService.tenantBrandingFavicon();
                    if (favicon) {
                        const link: HTMLLinkElement =
                            document.querySelector("link[rel*='icon']") ||
                            document.createElement('link');
                        link.rel = 'icon';
                        link.href = favicon;
                        document.head.appendChild(link);
                    }

                    const unprotectedPaths = [
                        '/reset-password',
                        '/confirm-invitation',
                        '/broker',
                        '/client',
                        '/invoice',
                        '/thank-you',
                        '/reports',
                        '/client/reports',
                    ];
                    const isUnprotected = unprotectedPaths.some((p) =>
                        window.location.pathname.startsWith(p),
                    );

                    const token = localStorage.getItem(MEMBER_MANAGER_TOKEN);

                    if (token && !isUnprotected) {
                        try {
                            const meResponse = await firstValueFrom(
                                authDataService.getMe(token),
                            );

                            authStoreService.setState({
                                requestId: meResponse.request_id,
                                user: {
                                    companyId: meResponse?.user?.company.id,
                                    email: meResponse.user.email,
                                    id: meResponse.user.id,
                                    serviceGroupId:
                                        meResponse.user.service_group_id,
                                    firstName: meResponse.user.first_name,
                                    lastName: meResponse.user.last_name,
                                    roles: meResponse.user.roles,
                                },
                            });

                            const service = await firstValueFrom(
                                authDataService.getMyPermissions(
                                    environment.SERVICE_ID,
                                ),
                            );
                            if (service) {
                                permissionStoreService.setPermissions(
                                    service.permissions.map(
                                        (p) => p.permission_key,
                                    ),
                                );
                            }
                        } catch (error) {
                            console.error(
                                'Failed to restore session during init:',
                                error,
                            );
                            localStorage.removeItem(MEMBER_MANAGER_TOKEN);
                            localStorage.removeItem(MEMBER_MANAGER_REFRESH);
                            authStoreService.setState({
                                accessToken: '',
                                refreshToken: '',
                                requestId: '',
                                user: {
                                    companyId: 0,
                                    email: '',
                                    id: 0,
                                    serviceGroupId: 0,
                                    firstName: '',
                                    lastName: '',
                                    roles: [],
                                },
                            });

                            const redirectUrl = encodeURIComponent('/app');
                            await router.navigate(['/auth/sign-in'], {
                                replaceUrl: true,
                                queryParams: { redirectUrl },
                            });
                        }
                    } else {
                        if (!isUnprotected) {
                            const rawCurrentUrl =
                                window.location.pathname +
                                window.location.search +
                                window.location.hash;
                            const currentUrl = rawCurrentUrl.startsWith('/auth')
                                ? '/app'
                                : rawCurrentUrl;
                            const redirectUrl = encodeURIComponent(currentUrl);

                            await router.navigate(['/auth/sign-in'], {
                                replaceUrl: true,
                                queryParams: { redirectUrl },
                            });
                        }
                    }
                }

                /*
                const authService = inject(AuthService);
                const designService = inject(DesignService);
                const titleService: Title = inject(Title);
                const router: Router = inject(Router);
                const analytics = inject(AnalyticsService);
                const root: '/' = '/';

                const designSettings = await firstValueFrom(
                    designService.initializeDesignSettings(),
                );

                if (designSettings?.name) {
                    const title = `${designSettings.name} | Member Manager`;
                    titleService.setTitle(title);
                }

                if (designSettings?.measurement_id) {
                    analytics.init(designSettings?.measurement_id);
                }

                if (designSettings.company_prefix) {
                    const link: HTMLLinkElement =
                        document.querySelector("link[rel*='icon']") ||
                        document.createElement('link');
                    link.type = 'image/png';
                    link.rel = 'shortcut icon';
                    link.href = `/assets/fav-icons/${designSettings.company_prefix}.png`;
                    document.getElementsByTagName('head')[0].appendChild(link);
                }

                if (
                    window.location.pathname === root ||
                    window.location.pathname.startsWith('/app')
                ) {
                    const token = await firstValueFrom(authService.token$);

                    if (token) {
                        await firstValueFrom(authService.fetchMe());
                        if (window.location.pathname === root) {
                            router.navigate(['/app']);
                        }
                    } else {
                        const redirectUrl =
                            window.location.pathname === root
                                ? '/app'
                                : window.location.pathname + window.location.search;
                                await router.navigate(['/auth'], {
                            replaceUrl: true,
                            queryParams: { redirectUrl },
                        });
                    }
                }
                */

                resolve();
            } catch (error) {
                console.error('Error during app initialization:', error);
                resolve();
            }
        });
    };
}
